// T270(4) — the "why this job" bullets, once.
//
// The desktop row and the mobile card each rendered their own version of the
// same two blocks (the rule-based reasons/risks, and the AI explanation), and
// they had drifted in every way two copies can:
//
//   - the row showed 3 reasons and 2 risks, the card 2 and 1
//   - the row showed missingSkills, the card did not
//   - the row rendered EVERY AI bullet while the card capped at 2 and 1, so one
//     explained rec could be four times the height of its neighbours (T256)
//   - emphasizeInCv appeared only on the row, riskFlags only on the row
//   - the T220 comment was pasted into both, identically
//
// One component. `dense` picks between the two caps, declared side by side so
// the difference between a phone row and a table row is a number, not a fork.
const CAPS = {
  //        reasons risks skills whyMatch whatsMissing
  full: { reasons: 3, risks: 2, skills: 5, why: 2, missing: 1 },
  dense: { reasons: 2, risks: 1, skills: 0, why: 2, missing: 1 },
};

export default function RecInsights({ rec, dense = false }) {
  const cap = dense ? CAPS.dense : CAPS.full;
  const exp = rec.llmExplanation;
  const hasRules = rec.reasons?.length > 0 || rec.risks?.length > 0 || (cap.skills > 0 && rec.missingSkills?.length > 0);
  if (!hasRules && !exp) return null;

  // How much the caps are hiding. Silence about truncation is what let the
  // uncapped desktop version look correct for so long.
  const hiddenAi =
    Math.max(0, (exp?.whyMatch?.length ?? 0) - cap.why) + Math.max(0, (exp?.whatsMissing?.length ?? 0) - cap.missing);

  return (
    <>
      {hasRules && (
        <div className="mt-1.5 space-y-0.5 text-xs">
          {rec.reasons?.slice(0, cap.reasons).map((r, i) => (
            <div key={`r${i}`} className="text-emerald-300">+ {r}</div>
          ))}
          {rec.risks?.slice(0, cap.risks).map((r, i) => (
            <div key={`x${i}`} className="text-amber-300">– {r}</div>
          ))}
          {cap.skills > 0 && rec.missingSkills?.length > 0 && (
            <div className="text-slate-500">
              missing: <span className="text-slate-400">{rec.missingSkills.slice(0, cap.skills).join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {exp && (
        <div className="mt-2 space-y-1 rounded border border-brand-700 bg-brand-600/5 p-2 text-xs">
          <div className="text-[10px] uppercase tracking-wide text-brand-500">✨ AI</div>
          {exp.whyMatch?.slice(0, cap.why).map((b, i) => (
            <div key={`w${i}`} className="text-emerald-300">+ {b}</div>
          ))}
          {exp.whatsMissing?.slice(0, cap.missing).map((b, i) => (
            <div key={`m${i}`} className="text-amber-300">– {b}</div>
          ))}
          {hiddenAi > 0 && <div className="text-[10px] text-slate-500">+{hiddenAi} more — open the row</div>}
          {!dense && exp.emphasizeInCv?.length > 0 && (
            <div className="text-slate-300">
              <span className="font-medium">Emphasize:</span> {exp.emphasizeInCv.join(' · ')}
            </div>
          )}
          {exp.interviewQuestion && (
            <div className="text-slate-400">
              <span className="font-medium text-slate-300">Interview Q:</span> {exp.interviewQuestion}
            </div>
          )}
          {!dense && exp.riskFlags?.length > 0 && (
            <div className="space-y-0.5">
              {exp.riskFlags.map((b, i) => (
                <div key={`rf${i}`} className="text-red-400">⚠ {b}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
